import { showMobile } from "helpers/adaptation";
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import TabPackagesCourses from "./TabPackagesCourses";
import { useTypedDispatch, useTypedSelector } from "store";
import {
  getPackages,
  getPackageCourses,
  getExercisesMixCoursesForPackage,
  getBidsMixCoursesForPackage
} from "store/users/thunkActions";
import CourseItem from "common/components/CourseItem";
import CardCourseIcon from "assets/images/package-basic.svg";
import InteractiveIcon from "assets/images/packageInteractiveIcon.svg";
import { setGame } from "store/game/slice";
import { setSimpleField, setTriggerAlert } from "store/users/slice";
import { localStorageKeys } from "config/localStorageKeys";
import { shuffleArray } from "common/helpers/arrayTools";
import "./stylesMobile.scss";

interface Props {
  searchData?: any;
  data?: any;
  searchText?: string;
}

const PracticePackagesExercises = ({ searchText, data }: Props) => {
  const [searchParams] = useSearchParams();
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { subscriptionValid } = useTypedSelector((store) => store.user.client);

  const getInitialTab = () => {
    const subTab = searchParams.get("tab");
    if (subTab === "bidding_interactive") {
      return "bidding_interactive";
    }
    return "bidding";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [basicPackages, setBasicPackages] = useState<any[]>([]);
  const [interactivePackages, setInteractivePackages] = useState<any[]>([]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const basicRes: any = await dispatch(getPackages(10));
        const interactiveRes: any = await dispatch(getPackages(20));

        setBasicPackages(basicRes || []);
        setInteractivePackages(interactiveRes || []);
      } catch (err) {
        console.error("Error fetching packages:", err);
      }
    };

    fetchPackages();
  }, [dispatch]);

  const listToDisplay = useMemo(() => {
    const baseList = activeTab === "bidding" ? basicPackages : interactivePackages;

    if (!searchText || searchText.trim() === "") {
      return baseList;
    }

    const searchLower = searchText.toLowerCase();
    return baseList.filter((packageItem: any) =>
      packageItem?.name?.toLowerCase().includes(searchLower)
    );
  }, [activeTab, basicPackages, interactivePackages, searchText]);

  const onPressItem = async (packageItem: any) => {
    if (!subscriptionValid) {
      return dispatch(
        setSimpleField({
          type: "blockMixCourses",
          value: true
        })
      );
    }

    try {
      dispatch(setSimpleField({ type: "isLoading", value: true }));

      const packageType = activeTab === "bidding" ? 10 : 20;
      const packageCoursesRes: any = await dispatch(
        getPackageCourses(packageItem?.id, packageType)
      );

      if (!packageCoursesRes?.courses?.length) {
        return dispatch(
          setTriggerAlert({
            show: true,
            title: "This package is empty"
          })
        );
      }

      const courses = packageCoursesRes.courses;
      localStorage.setItem(localStorageKeys.mixCoursesListID, JSON.stringify(courses));

      // Store courses in setAllCoursesBidding format for course name lookup
      const isInteractive = activeTab === "bidding_interactive";
      const coursesForLookup = courses.map((course: any) => ({
        Id: course.id,
        Name: course.name,
        IsInteractive: isInteractive
      }));
      localStorage.setItem(localStorageKeys.setAllCoursesBidding, JSON.stringify(coursesForLookup));

      // Step 3: Get exercises for all courses in package
      let resCourse;
      if (activeTab === "bidding") {
        resCourse = await dispatch(getExercisesMixCoursesForPackage(courses));
      } else {
        resCourse = await dispatch(getBidsMixCoursesForPackage(courses));
      }

      if (!resCourse?.Results?.length) {
        return dispatch(
          setTriggerAlert({
            show: true,
            title: "This package has no exercises"
          })
        );
      }

      const resLevels = localStorage.getItem("levels");
      const audit: number[] = [];
      if (resLevels) {
        const valLevel = JSON.parse(resLevels);
        if (valLevel?.ease) audit.push(10);
        if (valLevel?.medium) audit.push(20);
        if (valLevel?.hard) audit.push(30);
      }

      let filteredList = [];
      if (audit.length) {
        resCourse?.Results?.forEach((val) => {
          if (audit.find((valFind) => `${val?.Data?.Level}`.indexOf(`${valFind}`) !== -1)) {
            filteredList.push(val);
          }
        });
      } else {
        filteredList = [...(resCourse?.Results || [])];
      }

      if (!filteredList.length) {
        return dispatch(
          setTriggerAlert({
            show: true,
            title: "אין תרגילים ברמות שנבחרו"
          })
        );
      }

      const shuffleList = shuffleArray([...filteredList]);
      localStorage.setItem("listMixBidding", JSON.stringify(shuffleList));

      // Use shuffleList[0] to ensure pagination starts from 1
      const firstExercise = shuffleList[0];
      const gameData = {
        gameId: firstExercise?.Data?.Id || firstExercise?.Data?.Id_Bid,
        courseId: firstExercise?.Data?.CourseId,
        gameName: firstExercise?.Data?.Name,
        gameTitle: packageItem?.name,
        mixCourses: courses,
        type: activeTab === "bidding" ? "bidding" : "interactive-bidding"
      };

      localStorage.setItem("bidding", JSON.stringify(gameData));
      dispatch(setGame(gameData));

      if (activeTab === "bidding") {
        navigate(`/bidding?type=70&typeBidding=package&tab=${activeTab}`);
      } else {
        navigate(`/bidding_interactive?type=70&typeBidding=package&tab=${activeTab}`);
      }
    } catch (err) {
      console.error("Error opening package:", err);
      dispatch(
        setTriggerAlert({
          show: true,
          title: "Error opening package"
        })
      );
    } finally {
      dispatch(setSimpleField({ type: "isLoading", value: false }));
    }
  };

  return (
    <>
      {!showMobile && (
        <div className="PracticeByCourses-main">
          <div className="PracticeByCoursesTab-ul">
            <TabPackagesCourses
              onSelect={(val) => {
                setActiveTab(val);
              }}
              activeTab={activeTab}
            />
            <div className="container-list-mix container-list-packages">
              {listToDisplay.map((val, index) => {
                if (!val) {
                  return null;
                }
                const isInteractive = activeTab === "bidding_interactive";
                return (
                  <div key={`${val?.id}_${index}_package`} className="bidding-item-mix">
                    <CourseItem
                      onPressItem={() => onPressItem(val)}
                      isInteractive={isInteractive}
                      title={val?.name}
                      img={isInteractive ? InteractiveIcon : CardCourseIcon}
                      isBidPlay={false}
                      isDisabled={false}
                      iconClassName="package-icon"
                      courses={val?.courses}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showMobile && (
        <div className="PracticeByCourses-main-mobile">
          <div className="PracticeByCoursesTab-ul">
            <TabPackagesCourses
              onSelect={(val) => {
                setActiveTab(val);
              }}
              activeTab={activeTab}
              isMobile
            />
            <div className="practiceByCoursesTab-ul-container">
              {listToDisplay.map((val, index) => {
                if (!val) {
                  return null;
                }
                const isInteractive = activeTab === "bidding_interactive";
                return (
                  <CourseItem
                    key={`${val?.id}_${index}_package`}
                    onPressItem={() => onPressItem(val)}
                    isInteractive={isInteractive}
                    title={val?.name}
                    img={isInteractive ? InteractiveIcon : CardCourseIcon}
                    isBidPlay={false}
                    isDisabled={false}
                    iconClassName="package-icon-mobile"
                    courses={val?.courses}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PracticePackagesExercises;
